import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { getCancellationBeforeDate, shoppingApiDateFormatFromLocale } from '@/app/components/hotel-detail/common/dates';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import type { Brand } from '@/domainLogic/index.ts';
import { Country, Language } from '@/domainLogic/index.ts';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import {
  buildCancellationPoliciesByRange,
  cancellationPoliciesTranslationKeys,
  groupRefundableByUpToDate,
} from './cancellationPolicies';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import OpenXModal from '../../search/components/Modal/OpenXModal';
import BasicModalHeader from '../../search/components/Modal/LayoutElements/BasicModalHeader';

const Bullet = styled.div`
  background-color: ${(p) => p.theme.commons.colors.gray800};
  border-radius: 100%;
  width: 10px;
  height: 10px;
  margin: 7px 7px 5px 0;
`;

const VerticalDottedLine = styled.div`
  border-left: 2px dashed ${(p) => p.theme.commons.colors.gray100};
  margin-left: 3.5px;
  margin-bottom: -17px;
  flex-grow: 1;
`;

const Separator = styled.hr`
  margin: 16px -16px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: white;
  padding: 16px;
  border-radius: 8px;
`;

type CancellationPoliciesModalProps = {
  id: string;
  mealPlans: readonly MealPlanType[];
  isOpen: boolean | undefined;
  onDismiss: () => void;
  onOpenByBrowserHistory?: () => void;
};

export function CancellationPoliciesModal(props: CancellationPoliciesModalProps) {
  const { id, mealPlans, isOpen, onDismiss, onOpenByBrowserHistory } = props;
  const { t } = useTranslation();
  const { staticData, commonData } = usePageWrapperData();
  const isMobile = commonData.isMobileDevice;

  return (
    <OpenXModal
      isOpen={isOpen ?? false}
      isMobile={isMobile ?? true}
      brand={staticData.domainValues?.brand as Brand}
      onClose={onDismiss}
      onCloseByBrowserHistory={onDismiss}
      onOpenByBrowserHistory={onOpenByBrowserHistory}
      fullScreen={false}
      id={`taxes-modal-${id}`}
      aria-label={t('vacation.select.room_panel.cancellation')}
      bodyBackgroundColor="backgroundLayer01"
      header={{
        sticky: true,
        element: (
          <BasicModalHeader handleClose={onDismiss}>{t('vacation.select.room_panel.cancellation')}</BasicModalHeader>
        ),
      }}
    >
      <CancellationPolicyModalContent mealPlans={mealPlans} />
    </OpenXModal>
  );
}

export function CancellationPolicyModalContent({ mealPlans }: { mealPlans: readonly MealPlanType[] }) {
  const nonRefundable = mealPlans.filter(({ cancellationPolicy }) => cancellationPolicy.type === 'NOT_REFUNDABLE');
  const refundable = mealPlans.filter(({ cancellationPolicy }) => cancellationPolicy.type === 'REFUNDABLE');
  const { t } = useTranslation();
  const { accommodationOnly, isTrain } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { country, lang } = localization;

  // To show different free cancellation dates in different blocks
  const refundableByDateArray = groupRefundableByUpToDate(refundable);

  return (
    <div
      css={css`
        font-size: ${(p) => p.theme.commons.font.sizes.base};
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      {nonRefundable.length ? <CancellationPolicyContent type="NOT_REFUNDABLE" mealPlans={nonRefundable} /> : null}
      {refundableByDateArray.length
        ? refundableByDateArray.map((refundableByDateMealPlans, index) => (
            <CancellationPolicyContent
              key={refundableByDateMealPlans[0].id}
              type="REFUNDABLE"
              mealPlans={refundableByDateMealPlans}
              hideDescription={index > 0}
              showDateInTitle={refundableByDateArray.length > 1}
            />
          ))
        : null}
      {!accommodationOnly && (
        <Text variant="Title4">
          {t(
            isTrain
              ? 'vacation.common.mealplan.room.cancelpolicy.train.title'
              : 'vacation.common.mealplan.room.cancelpolicy.flight.title'
          )}
        </Text>
      )}
      {!accommodationOnly && (
        <SectionContainer>
          <TextBase>
            {t(
              isTrain
                ? 'vacation.common.mealplan.room.cancelpolicy.train.description'
                : 'vacation.common.mealplan.room.cancelpolicy.flight.description'
            )}
          </TextBase>
        </SectionContainer>
      )}

      {accommodationOnly === false && (nonRefundable.length > 0 || refundableByDateArray.length > 0) && (
        <div>
          <div
            css={css`
              font-size: ${(p) => p.theme.commons.font.sizes.small};
              color: ${(p) => p.theme.commons.colors.gray400};
            `}
          >
            {country === Country.US && lang === Language.ES
              ? t('vacation.common.mealplan.room.cancelpolicy.disclaimer.es_us')
              : t('vacation.common.mealplan.room.cancelpolicy.disclaimer')}
          </div>
        </div>
      )}
    </div>
  );
}

function CancellationPolicyContent({
  type,
  mealPlans,
  hideDescription = false,
  showDateInTitle = false,
}: {
  type: CancellationType;
  mealPlans: MealPlanType[];
  hideDescription?: boolean;
  showDateInTitle?: boolean;
}) {
  const { t } = useTranslation();
  const { accommodationOnly } = useUrlSearchParams();
  const { localization } = usePageWrapperData();
  const { locale } = localization;

  const cancellationPoliciesByRange = buildCancellationPoliciesByRange(mealPlans, t, accommodationOnly);

  const cancellationPoliciesValuesSameRanges = Object.values(cancellationPoliciesByRange);
  const translationKeys = cancellationPoliciesTranslationKeys[type === 'UNKNOWN' ? 'NOT_REFUNDABLE' : type];

  return (
    <>
      <Text variant="Title4">{`${t(accommodationOnly ? translationKeys.titleHO : translationKeys.titleDP)}${showDateInTitle && mealPlans[0].cancellationPolicy.upTo ? ` ${t('vacation.common.mealplan.room.cancellationtype.refundable.before', { date: shoppingApiDateFormatFromLocale(getCancellationBeforeDate(mealPlans[0].cancellationPolicy.upTo), locale, 'dd/MM/yyyy') })}` : ''}`}</Text>
      <SectionContainer>
        {!hideDescription && <TextBase>{t(translationKeys.description)}</TextBase>}

        {cancellationPoliciesValuesSameRanges.map((cancellationPoliciesSameRange) => {
          const cancellationPoliciesValues = Object.values(cancellationPoliciesSameRange);

          return (
            <div
              key={`ranges-${cancellationPoliciesValues[0].range.from}`}
              css={`
                display: flex;
                flex-direction: column;
                gap: 16px;

                &:last-child {
                  ${Separator} {
                    display: none;
                  }
                }
              `}
            >
              {cancellationPoliciesValues.map(({ range, type: cancellationType, cancellationPolicies }, index) => {
                const parsedCancellationDate =
                  cancellationType === 'FREE_CANCELLATION'
                    ? // Free-cancellation copy speaks "before X", where X is the day AFTER the last free day.
                      shoppingApiDateFormatFromLocale(getCancellationBeforeDate(range.to), locale, 'dd/MM/yyyy')
                    : shoppingApiDateFormatFromLocale(range.from, locale, 'dd/MM/yyyy');
                const showDiscounts = cancellationPolicies.filter((policy) => !policy.discount).length > 0;
                return (
                  <div key={range.from}>
                    <div
                      css={`
                        display: flex;
                      `}
                    >
                      {type === 'REFUNDABLE' && (
                        <div
                          css={`
                            display: flex;
                            flex-direction: column;
                          `}
                        >
                          <Bullet />
                          {index < cancellationPoliciesValues.length - 1 && <VerticalDottedLine />}
                        </div>
                      )}
                      <div
                        css={`
                          display: flex;
                          flex-direction: column;
                          gap: 8px;
                        `}
                      >
                        <Text variant="Title5">
                          {cancellationType === 'FREE_CANCELLATION'
                            ? t('vacation.common.mealplan.room.cancelpolicy.datebefore', {
                                date: parsedCancellationDate,
                              })
                            : index < cancellationPoliciesValues.length - 1
                              ? t('vacation.select.cancellation_modal.title_from_to', {
                                  date1: shoppingApiDateFormatFromLocale(range.from, locale, 'dd/MM/yyyy'),
                                  date2: shoppingApiDateFormatFromLocale(range.to, locale, 'dd/MM/yyyy'),
                                })
                              : t('vacation.common.mealplan.room.cancelpolicy.dateafter', {
                                  date: parsedCancellationDate,
                                })}
                        </Text>
                        <TextBase>
                          {cancellationType === 'FREE_CANCELLATION'
                            ? accommodationOnly
                              ? t('vacation.common.mealplan.room.cancelpolicy.active.free.before', {
                                  from_date: parsedCancellationDate,
                                })
                              : t('vacation.common.mealplan.room.cancelpolicy.active.dp').replaceAll(
                                  '$from_date',
                                  parsedCancellationDate
                                )
                            : index < cancellationPoliciesValues.length - 1
                              ? t('vacation.select.cancellation_modal.description.title_from_to', {
                                  date1: shoppingApiDateFormatFromLocale(range.from, locale, 'dd/MM/yyyy'),
                                  date2: shoppingApiDateFormatFromLocale(range.to, locale, 'dd/MM/yyyy'),
                                })
                              : t('vacation.common.mealplan.room.cancelpolicy.active.date', {
                                  date: parsedCancellationDate,
                                })}
                        </TextBase>
                        {(type !== 'NOT_REFUNDABLE' || accommodationOnly) &&
                        (cancellationType !== 'FREE_CANCELLATION' ||
                          cancellationPoliciesValuesSameRanges.length > 1) ? (
                          <ul
                            css={`
                              list-style: none;
                            `}
                          >
                            {cancellationPolicies.map((policy) => (
                              <li
                                key={`${policy.boardClass}${policy.discount ? `-${policy.discount}` : ''}`}
                                css={css`
                                  display: flex;
                                  padding: 8px 0;
                                  align-items: baseline;
                                  column-gap: 4px;
                                  flex-wrap: wrap;
                                  border-bottom: 1px dashed ${(p) => p.theme.commons.colors.gray100};
                                `}
                              >
                                <TextBase
                                  css={`
                                    white-space: nowrap;
                                  `}
                                >
                                  {policy.boardClass}
                                </TextBase>
                                {showDiscounts && policy.discount ? (
                                  <TextBase
                                    css={css`
                                      font-size: ${(p) => p.theme.commons.font.sizes.small};
                                      white-space: nowrap;
                                    `}
                                  >
                                    ({policy.discount})
                                  </TextBase>
                                ) : null}

                                <TextBase
                                  css={css`
                                    flex-grow: 1;
                                  `}
                                />
                                <TextBase>{formatPrice({ price: policy?.amount ?? 0, localization })}</TextBase>
                              </li>
                            ))}
                          </ul>
                        ) : undefined}
                      </div>
                    </div>
                  </div>
                );
              })}
              <Separator />
            </div>
          );
        })}
      </SectionContainer>
    </>
  );
}
